<template>
  <Dialog
    v-model:visible="computedShowReAuthenticate"
    modal
    :closable="false"
    :draggable="false"

    :showHeader="false"
    :blockScroll="true"

    :pt="{
      root: { class: 'w-[95vw] md:max-w-[700px] max-h-[70vh]  ' },
      mask: { class: 'bg-black/80 backdrop-blur-xl' },
    }"
  >
    <div
      v-if="!isLoaded"
      class="flex justify-center items-center min-h-[200px]"
    >
      <ProgressSpinner />
    </div>

    <div v-else-if="!isSignedIn&& isLoaded">
      <div class="flex flex-col items-center space-y-4">
        <div class="flex items-center">
          <img
            src="https://cim-pro.co/wp-content/uploads/2025/01/cropped-CIM-PRO-New-Logo-White-BG.png"
            class="w-32 mb-2"
          >
        </div>
        <h2 class="text-2xl font-bold text-gray-900">
          Please sign in to continue
        </h2>
        <Button
          label="Sign In"
          class="w-full max-w-xs"
          @click="navigateToAuthApp"
        >
          <template #icon>
            <Icon
              name="heroicons:arrow-right-on-rectangle"
              class="w-4 h-4 mr-2"
            />
          </template>
        </Button>
      </div>
    </div>

    <div v-if="isSignedIn&& isLoaded">
      <div class="space-y-6 pt-8 ">
        <div class="flex flex-col items-center">
          <div class="flex items-center ">
            <img
              src="https://cim-pro.co/wp-content/uploads/2025/01/cropped-CIM-PRO-New-Logo-White-BG.png"
              class="w-32 mb-2"
            >
          </div>
          <h2 class="mt-4 text-center text-2xl font-bold text-gray-900">
            Choose Your Workspace
          </h2>
          <p class="my-2 text-center text-sm text-gray-600 ">
            Select a workspace to continue or sign out of your account
          </p>
        </div>
      </div>

      <div
        v-if="isLoading"
        class="flex justify-center items-center min-h-[200px]"
      >
        <ProgressSpinner />
      </div>

      <div v-else>
        <Tabs v-model:value="tabValue">
          <TabList>
            <Tab value="0">
              Workspaces ({{ workspaces.length }})
            </Tab>
            <Tab value="1">
              Invites ({{ invites.length }})
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel value="0">
              <div
                v-if="workspaces.length === 0"
                class="text-center min-h-[200px] flex flex-col items-center justify-center space-y-6"
              >
                <div class="flex flex-col items-center">
                  <Icon
                    name="heroicons:building-office-2"
                    class="w-20 h-20 text-gray-300"
                  />

                  <p class="mt-2 text-gray-600 max-w-sm">
                    You currently don't have access to any workspaces.
                  </p>
                </div>
              </div>

              <div
                v-else
                class=" min-h-[200px] "
              >
                <IconField
                  fluid
                  class="w-full"
                >
                  <InputIcon>
                    <Icon name="mdi-magnify" />
                  </InputIcon>
                  <InputText
                    v-model="workspaceSearchQuery"
                    fluid
                    placeholder="Search workspaces"
                    class="w-full "
                  />
                </IconField>
                <div class="overflow-y-auto   max-h-[400px] space-y-3 p-2">
                  <div
                    v-for="workspace in computedWorkspaces"
                    :key="workspace.id"
                    class="group relative p-4 bg-white rounded-xl shadow-sm ring-1 ring-gray-200 hover:shadow-md hover:ring-primary-500/50 transition-all duration-200 cursor-pointer"
                    @click="selectWorkspace(workspace)"
                  >
                    <div class="flex items-center justify-between">
                      <div class="min-w-0 flex-1 flex items-center gap-3">
                        <div class="h-10 w-10 flex-shrink-0">
                          <img
                            v-if="workspace.WorkSpace?.logo"
                            :src="workspace.WorkSpace.logo"
                            :alt="`${workspace.WorkSpace?.name} logo`"
                            class="h-full w-full object-contain rounded-md"
                          >
                          <div
                            v-else
                            class="h-full w-full bg-primary-100 rounded-md flex items-center justify-center"
                          >
                            <span class="text-lg font-bold text-primary-600">
                              {{ workspace.WorkSpace?.name?.[0]?.toUpperCase() }}
                            </span>
                          </div>
                        </div>
                        <div class="min-w-0 flex-1">
                          <h3 class="text-lg font-semibold text-gray-900 group-hover:text-primary-600 truncate">
                            {{ workspace.WorkSpace?.name }}
                          </h3>
                          <p class="text-sm text-gray-500 truncate">
                            Click to enter workspace
                          </p>
                        </div>
                      </div>
                      <div class="flex-shrink-0 self-center">
                        <Icon
                          name="heroicons:chevron-right"
                          class="h-5 w-5 text-gray-400 group-hover:text-primary-500 group-hover:translate-x-1 transition-all duration-200"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabPanel>

            <TabPanel value="1">
              <div
                v-if="invites.length === 0"
                class="text-center min-h-[200px] flex flex-col items-center justify-center space-y-6"
              >
                <Icon
                  name="heroicons:inbox"
                  class="w-20 h-20 text-gray-300"
                />
                <p class="mt-2 text-gray-600 max-w-sm">
                  You don't have any invites to any workspaces.
                </p>
              </div>

              <div v-else>
                <div class="overflow-y-auto max-h-[450px] space-y-3 p-2">
                  <div
                    v-for="invite in invites"
                    :key="invite.id"
                    class="p-4 bg-white rounded-xl shadow-sm ring-1 ring-gray-200 hover:shadow-md hover:ring-primary-500/50 transition-all duration-200 cursor-pointer"
                  >
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-3 w-full">
                        <div class="h-10 w-10 flex-shrink-0">
                          <img
                            v-if="invite.WorkSpace?.logo"
                            :src="invite.WorkSpace?.logo"
                            :alt="`${invite.WorkSpace?.name} logo`"
                            class="h-full w-full object-contain rounded-md"
                          >
                          <div
                            v-else
                            class="h-full w-full bg-primary-100 rounded-md flex items-center justify-center"
                          >
                            <span class="text-lg font-bold text-primary-600">
                              {{ invite.WorkSpace?.name?.[0]?.toUpperCase() }}
                            </span>
                          </div>
                        </div>

                        <div class="min-w-0 flex-1">
                          <h3 class="text-lg font-semibold text-gray-900 group-hover:text-primary-600 truncate">
                            {{ invite.WorkSpace?.name }}
                          </h3>
                        </div>

                        <div class="flex items-center gap-2">
                          <!-- <Button
                        severity="danger"
                        label="Decline"
                        outlined
                        @click="declineInvite(invite.id)"
                      >
                        <template #icon>
                          <Icon
                            name="heroicons:x-mark"
                          />
                        </template>
                      </Button> -->
                          <Button
                            :disabled="isAcceptingInvite"
                            label="Accept"
                            @click="handleAcceptInvite(invite)"
                          >
                            <template #icon>
                              <Icon
                                name="heroicons:check"
                              />
                            </template>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>
        <div class="pt-4 flex flex-col items-center gap-4 border-t border-gray-200 mt-4">
          <SignOutButton :redirect-url="`${authAppUrl}/auth/sign-in`">
            <Button outlined>
              <Icon
                name="heroicons:arrow-right-on-rectangle"
                class="w-4 h-4 mr-2"
              />
              Sign Out
            </Button>
          </SignOutButton>
          <p class="text-sm text-gray-600">
            Any questions? <a
              href="mailto:support@cim-pro.co"
              class="text-primary-600 hover:text-primary-700"
            >Email us</a>
          </p>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { useWorkSpace } from '~/composables/useWorkSpace'
import useSessionComposable from '~/composables/useSessionComposable'
import { useRoleStore } from '~/store/roleStore'

interface UserWorkSpace {
  id: string
  WorkSpace: {
    id: string
    name: string
    theme: string | null
    logo: string | null
  } | null
}

const { isSignedIn, user, isLoaded } = useUser()
const router = useRouter()
const { getAllWorkSpaces, setActiveWorkSpace, getUserInvites, acceptInvite } = useWorkSpace()
const { showSelectWorkSpace } = useSessionComposable()

const computedShowReAuthenticate = computed(() => showSelectWorkSpace.value)
const workspaces = ref<UserWorkSpace[]>([])
const isLoading = ref(true)

const authAppUrl = process.env.AUTH_APP_URL || 'https://auth.cimpro.app'

const workspaceSearchQuery = ref('')

const navigateToAuthApp = () => {
  window.location.href = `${authAppUrl}`
}

// Load workspaces when dialog is shown
// watch(() => computedShowReAuthenticate.value, async (newValue) => {
//   console.log('computedShowReAuthenticate', computedShowReAuthenticate.value)
//   if (newValue) {
//     console.log('newValue', newValue)
//     try {
//       const response = await getAllWorkSpaces()
//       console.log('getAllWorkSpaces', response)
//       workspaces.value = (response.data || []) as UserWorkSpace[]
//     }
//     catch (error) {
//       console.error('Failed to load workspaces:', error)
//     }
//     finally {
//       isLoading.value = false
//     }
//   }
// })

const computedWorkspaces = computed(() => {
  return workspaces.value.filter((workspace) => {
    return workspace.WorkSpace?.name.toLowerCase().includes(workspaceSearchQuery.value.toLowerCase())
  })
})

const getUserWorkspaces = async () => {
  try {
    isLoading.value = true
    const response = await getAllWorkSpaces()

    workspaces.value = (response.data || []) as UserWorkSpace[]
  }
  catch (error) {
    console.error('Failed to load workspaces:', error)
  }
  finally {
    isLoading.value = false
  }
}

const roleStore = useRoleStore()
// Handle workspace selection
const selectWorkspace = async (workspace: UserWorkSpace) => {
  if (!workspace.WorkSpace) {
    console.error('Invalid workspace data')
    return
  }

  try {
    await setActiveWorkSpace({ workSpaceId: workspace.WorkSpace.id })

    await roleStore.fetchUserRole()

    //  redirect to the home page

    reloadNuxtApp({
      path: '/',
      persistState: true,
    })
    //  reload the page
    // window.location.href = '/'
  }
  catch (error) {
    console.error('Failed to set active workspace:', error)
  }
}

const tabValue = ref('0')

const invites = ref<any[]>([])

const fetchInvites = async () => {
  isLoading.value = true
  try {
    const response = await getUserInvites()
    invites.value = (response.data || [])
  }
  catch (error) {
    console.error('Failed to get user invites:', error)
  }
}
const toast = useToast()
const isAcceptingInvite = ref(false)

const handleAcceptInvite = async (invite: any) => {
  try {
    isAcceptingInvite.value = true
    const response = await acceptInvite({ inviteId: invite.id })
    await fetchInvites()
    await getUserWorkspaces()
    tabValue.value = '0'
    isLoading.value = false
    toast.add({
      severity: 'success',
      summary: 'Invite accepted',
      detail: 'You have been added to the workspace',
    })
  }
  catch (error) {
    console.error('Failed to accept invite:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to accept invite',
    })
  }
  finally {
    isAcceptingInvite.value = false
  }
}

onMounted(async () => {
  await fetchInvites()
  await getUserWorkspaces()
})
</script>

<style scoped>
.group:hover .group-hover\:translate-x-0\.5 {
  transform: translateX(2px);
}
</style>
